import type { PersonioSyncItemSchema } from "@msuite/utils/schemas";
import type {
	PersonioSyncList,
	PersonioSyncListItem,
	PersonioSyncResult,
	PersonioSyncResultItem,
} from "@msuite/utils/types";

/** What the last sync answered, kept to show beside the rows it ran for. */
export interface PersonioSyncRun {
	results: PersonioSyncResult;
	areTeamsSynced: boolean;
}

/** What the office chose for a row instead of the server's plan. */
export type PersonioSyncOverride =
	| { action: "link"; userId: string; userName: string }
	| { action: "create" };

export type PersonioSyncOverrides = Record<string, PersonioSyncOverride>;

/** What pressing sync does with a row; null when nothing can be done with it. */
export type PersonioSyncAction = "link" | "create" | "adopt" | null;

/** A table row: the planned row, with the office's choice and the last sync's result laid over it. */
export interface PersonioSyncRow extends PersonioSyncListItem {
	action: PersonioSyncAction;
	/** The member the row is linked to, or will be linked to. */
	targetUserId: string | null;
	targetUserName: string | null;
	isOverridden: boolean;
	result: PersonioSyncResultItem | null;
}

/**
 * Lay the office's choices and the last sync's results over the server's plan.
 *
 * @param people - The planned rows, as the server sent them.
 * @param overrides - What the office chose by hand, per Personio id.
 * @param results - The last sync's results, if one ran while the table was open.
 * @returns One table row per planned row, in the server's order.
 */
export function toPersonioSyncRows(
	people: PersonioSyncList | undefined,
	overrides: PersonioSyncOverrides,
	results: PersonioSyncResult | null,
): PersonioSyncRow[] {
	const resultsById = new Map<string, PersonioSyncResultItem>();
	for (const result of results ?? []) resultsById.set(result.personId, result);

	const rows: PersonioSyncRow[] = [];
	for (const person of people ?? []) {
		const override = overrides[person.id] ?? null;
		rows.push({
			...person,
			...getAction(person, override),
			isOverridden: override !== null,
			result: resultsById.get(person.id) ?? null,
		});
	}
	return rows;
}

/**
 * The items a sync sends for the selected rows: a link names its member, and anything else is
 * left to the server to create or take over.
 *
 * @param rows - The table rows.
 * @param selectedIds - The ids of the selected rows.
 * @returns One item per selected row that has an action.
 */
export function toPersonioSyncItems(
	rows: PersonioSyncRow[],
	selectedIds: string[],
): PersonioSyncItemSchema[] {
	const selected = new Set(selectedIds);
	const items: PersonioSyncItemSchema[] = [];
	for (const row of rows) {
		if (!selected.has(row.id) || !row.action) continue;
		items.push({ personId: row.id, userId: row.action === "link" ? row.targetUserId : null });
	}
	return items;
}

function getAction(
	person: PersonioSyncListItem,
	override: PersonioSyncOverride | null,
): Pick<PersonioSyncRow, "action" | "targetUserId" | "targetUserName"> {
	if (override?.action === "link") {
		return { action: "link", targetUserId: override.userId, targetUserName: override.userName };
	}
	if (override?.action === "create") {
		return { action: "create", targetUserId: null, targetUserName: null };
	}

	switch (person.state) {
		case "link":
			return { action: "link", targetUserId: person.userId, targetUserName: person.userName };
		case "create":
		case "adopt":
			return { action: person.state, targetUserId: null, targetUserName: null };
		case "linked":
			return { action: null, targetUserId: person.userId, targetUserName: person.userName };
		default:
			return { action: null, targetUserId: null, targetUserName: null };
	}
}

/**
 * The members the picker leaves out for one row: whoever another row is already linked to or
 * about to be linked to, since one member stands for one Personio person.
 *
 * @param rows - The table rows.
 * @param personId - The row the picker is open for, whose own target stays pickable.
 * @returns The user ids to leave out.
 */
export function getTakenUserIds(rows: PersonioSyncRow[], personId: string): string[] {
	const taken: string[] = [];
	for (const row of rows) {
		if (row.id === personId || !row.targetUserId) continue;
		if (row.state === "linked" || row.action === "link") taken.push(row.targetUserId);
	}
	return taken;
}

/**
 * Count the outcomes of a sync for its summary line.
 *
 * @param results - The sync's results.
 * @returns How many were created, taken over, linked and failed.
 */
export function countOutcomes(results: PersonioSyncResult) {
	const counts = { created: 0, adopted: 0, linked: 0, failed: 0 };
	for (const result of results) counts[result.outcome] += 1;
	return counts;
}
